import React from "react";
import { Table, Tag, Button, Space, Tooltip } from "antd";
import {
  EyeOutlined,
  LinkOutlined,
  DisconnectOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import FinanceService from "../../../dependencies/custom/financeService";

/**
 * Students Table
 * Displays all students with admission status and parent account status
 */
const StudentsTable = ({
  data = [],
  loading = false,
  onViewFees,
  onLink,
  onUnlink,
  onEditLink,
  onAddSurePayCode,
  onChangeSurePayCode,
  onRemoveSurePayCode,
  pagination = {},
  onPaginationChange,
}) => {
  const columns = [
    {
      title: "Student",
      key: "studentName",
      render: (_, record) => {
        const fullName =
          `${record.lastName || ""} ${record.firstName || ""} ${record.middleName || ""}`.trim();
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{fullName}</div>
          </div>
        );
      },
      width: 200,
    },
    {
      title: "SPC",
      key: "spcCode",
      dataIndex: "spcCode",
      render: (text) => text || "-",
      width: 170,
    },
    {
      title: "SurePay Code",
      key: "surePayStudentCode",
      dataIndex: "surePayStudentCode",
      render: (code, record) => (
        <Space size="small">
          {code ? <Tag color="blue">{code}</Tag> : <Tag>Not assigned</Tag>}
          <Tooltip title={code ? "Change SurePay code" : "Add SurePay code"}>
            <Button
              type="link"
              size="small"
              icon={code ? <EditOutlined /> : <PlusOutlined />}
              onClick={() =>
                code
                  ? onChangeSurePayCode && onChangeSurePayCode(record)
                  : onAddSurePayCode && onAddSurePayCode(record)
              }
            />
          </Tooltip>
          {code && (
            <Tooltip title="Remove SurePay code">
              <Button
                type="link"
                size="small"
                danger
                aria-label={`Remove SurePay code ${code}`}
                icon={<DeleteOutlined />}
                onClick={() =>
                  onRemoveSurePayCode && onRemoveSurePayCode(record)
                }
              />
            </Tooltip>
          )}
        </Space>
      ),
      width: 240,
    },
    {
      title: "Class",
      key: "className",
      dataIndex: "className",
      render: (text) => text || "-",
      width: 100,
    },
    {
      title: "Stream",
      key: "streamName",
      dataIndex: "streamName",
      render: (text) => text || "-",
      width: 100,
    },
    {
      title: "Day/Boarding",
      key: "dayBoarding",
      dataIndex: "dayBoarding",
      render: (type) => (
        <Tag color={type === "Boarding" ? "purple" : "cyan"}>{type || "-"}</Tag>
      ),
      width: 100,
    },
    {
      title: "Admission",
      key: "admission_status",
      dataIndex: "admission_status",
      render: (status) => {
        const colors = {
          DRAFT: "default",
          SUBMITTED: "processing",
          REJECTED: "error",
          ADMITTED: "success",
          APPROVED: "success",
          "WAITING LIST": "warning",
          VERIFIED: "blue",
          INTERVIEW: "orange",
        };
        return <Tag color={colors[status] || "default"}>{status || "-"}</Tag>;
      },
      width: 110,
    },
    {
      title: "Link Status",
      key: "linkStatus",
      render: (_, record) => {
        if (record.hasParentAccount) {
          return (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Linked
            </Tag>
          );
        }
        return (
          <Tag color="red" icon={<CloseCircleOutlined />}>
            Not Linked
          </Tag>
        );
      },
      width: 100,
    },
    {
      title: "Parent",
      key: "parentAccount",
      render: (_, record) => {
        if (record.hasParentAccount && record.parentAccount) {
          return (
            record.parentAccount.fullName || record.parentAccount.email || "-"
          );
        }
        return "-";
      },
      width: 140,
    },
    {
      title: "Balance",
      key: "balance",
      dataIndex: "balance",
      render: (balance) => {
        const balanceValue = parseFloat(balance) || 0;
        return (
          <span
            style={{
              fontWeight: 500,
              color: balanceValue > 0 ? "#f5222d" : "#52c41a",
            }}
          >
            {FinanceService.formatCurrency(balanceValue)}
          </span>
        );
      },
      align: "right",
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Fees">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onViewFees && onViewFees(record)}
            />
          </Tooltip>
          {record.hasParentAccount ? (
            <>
              <Tooltip title="Change Parent Account">
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => onEditLink && onEditLink(record)}
                />
              </Tooltip>
              <Tooltip title="Unlink Parent Account">
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DisconnectOutlined />}
                  onClick={() => onUnlink && onUnlink(record)}
                />
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Link Parent Account">
              <Button
                type="link"
                size="small"
                style={{ color: "#52c41a" }}
                icon={<LinkOutlined />}
                onClick={() => onLink && onLink(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey={(record) => record.id}
      loading={loading}
      pagination={{
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 10,
        total: pagination.total || 0,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} student(s)`,
        responsive: true,
        onChange: (page, pageSize) =>
          onPaginationChange && onPaginationChange(page, pageSize),
      }}
      scroll={{ x: 1500 }}
      size="middle"
      style={{ marginTop: 16 }}
    />
  );
};

export default StudentsTable;
